import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, ShieldCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";

interface ProviderDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  complianceId: string;
}

const statusColors: Record<string, string> = {
  active: "bg-emerald-100 text-emerald-700",
  pending_verification: "bg-amber-100 text-amber-700",
  expired: "bg-red-100 text-red-700",
  revoked: "bg-red-100 text-red-700",
};

export default function ProviderDetailSheet({
  open,
  onOpenChange,
  complianceId,
}: ProviderDetailSheetProps) {
  const [addLicenseOpen, setAddLicenseOpen] = useState(false);
  const [newLicense, setNewLicense] = useState({
    state: "",
    license_number: "",
    issue_date: "",
    expiration_date: "",
    imlc_compact: false,
  });
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ["provider-detail", complianceId],
    queryFn: async () => {
      const [compRes, licRes] = await Promise.all([
        supabase.from("provider_compliance").select("*").eq("id", complianceId).single(),
        supabase.from("provider_licenses").select("*").eq("compliance_id", complianceId).order("expiration_date"),
      ]);

      let providerName = "Unknown";
      if (compRes.data) {
        const { data: prov } = await supabase
          .from("providers")
          .select("display_name, specialty")
          .eq("id", compRes.data.provider_id)
          .single();
        if (prov) providerName = prov.display_name;
      }

      return {
        compliance: compRes.data,
        licenses: licRes.data || [],
        providerName,
      };
    },
    enabled: open && !!complianceId,
  });

  const addLicenseMutation = useMutation({
    mutationFn: async (license: typeof newLicense) => {
      const { error } = await supabase.from("provider_licenses").insert({
        compliance_id: complianceId,
        state: license.state,
        license_number: license.license_number,
        issue_date: license.issue_date || null,
        expiration_date: license.expiration_date || null,
        imlc_compact: license.imlc_compact,
        license_status: "pending_verification",
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("License added");
      setAddLicenseOpen(false);
      setNewLicense({ state: "", license_number: "", issue_date: "", expiration_date: "", imlc_compact: false });
      queryClient.invalidateQueries({ queryKey: ["provider-detail", complianceId] });
      queryClient.invalidateQueries({ queryKey: ["provider-database"] });
      queryClient.invalidateQueries({ queryKey: ["compliance-dashboard"] });
    },
    onError: (error: any) => toast.error(error.message),
  });

  const deleteLicenseMutation = useMutation({
    mutationFn: async (licenseId: string) => {
      const { error } = await supabase.from("provider_licenses").delete().eq("id", licenseId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("License removed");
      queryClient.invalidateQueries({ queryKey: ["provider-detail", complianceId] });
      queryClient.invalidateQueries({ queryKey: ["provider-database"] });
    },
  });

  const comp = data?.compliance;
  const licenses = data?.licenses || [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" />
            {data?.providerName || "Provider Details"}
          </SheetTitle>
        </SheetHeader>

        {comp && (
          <div className="space-y-6 mt-6">
            {/* Profile Info */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">NPI Number</p>
                <p className="font-medium font-mono">{comp.npi_number || "—"}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Tax ID</p>
                <p className="font-medium font-mono">{comp.tax_id || "—"}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Specialty</p>
                <p className="font-medium">{comp.specialty || "—"}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Primary State</p>
                <p className="font-medium">{comp.primary_state || "—"}</p>
              </div>
              <div>
                <p className="text-muted-foreground">DEA Number</p>
                <p className="font-medium font-mono">{comp.dea_number || "—"}</p>
              </div>
              <div>
                <p className="text-muted-foreground">CAQH ID</p>
                <p className="font-medium font-mono">{comp.caqh_id || "—"}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Board Certification</p>
                <p className="font-medium">{comp.board_certification || "—"}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Background Check</p>
                <p className="font-medium capitalize">{comp.background_check_status || "—"}</p>
              </div>
            </div>

            <Separator />

            {/* Licenses */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold">State Licenses ({licenses.length})</h3>
                <Dialog open={addLicenseOpen} onOpenChange={setAddLicenseOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline">
                      <Plus className="h-4 w-4 mr-1" />
                      Add License
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add State License</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>State *</Label>
                          <Input
                            value={newLicense.state}
                            onChange={(e) => setNewLicense((l) => ({ ...l, state: e.target.value.toUpperCase() }))}
                            placeholder="MN"
                            maxLength={2}
                          />
                        </div>
                        <div>
                          <Label>License Number *</Label>
                          <Input
                            value={newLicense.license_number}
                            onChange={(e) => setNewLicense((l) => ({ ...l, license_number: e.target.value }))}
                            placeholder="License #"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Issue Date</Label>
                          <Input
                            type="date"
                            value={newLicense.issue_date}
                            onChange={(e) => setNewLicense((l) => ({ ...l, issue_date: e.target.value }))}
                          />
                        </div>
                        <div>
                          <Label>Expiration Date</Label>
                          <Input
                            type="date"
                            value={newLicense.expiration_date}
                            onChange={(e) => setNewLicense((l) => ({ ...l, expiration_date: e.target.value }))}
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={newLicense.imlc_compact}
                          onChange={(e) => setNewLicense((l) => ({ ...l, imlc_compact: e.target.checked }))}
                          className="rounded"
                        />
                        <Label>IMLC Compact License</Label>
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setAddLicenseOpen(false)}>
                          Cancel
                        </Button>
                        <Button
                          onClick={() => addLicenseMutation.mutate(newLicense)}
                          disabled={!newLicense.state || !newLicense.license_number}
                        >
                          Add License
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {licenses.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No licenses added yet
                </p>
              ) : (
                <div className="space-y-2">
                  {licenses.map((lic) => (
                    <Card key={lic.id}>
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-sm">{lic.state}</span>
                              <span className="text-sm font-mono text-muted-foreground">
                                #{lic.license_number}
                              </span>
                              {lic.imlc_compact && (
                                <Badge variant="outline" className="text-xs">IMLC</Badge>
                              )}
                            </div>
                            <div className="flex gap-3 text-xs text-muted-foreground mt-1">
                              {lic.issue_date && <span>Issued: {format(new Date(lic.issue_date), "MMM d, yyyy")}</span>}
                              {lic.expiration_date && (
                                <span>Expires: {format(new Date(lic.expiration_date), "MMM d, yyyy")}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={statusColors[lic.license_status] || "bg-muted"}>
                              {lic.license_status.replace("_", " ")}
                            </Badge>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-red-500"
                              onClick={() => deleteLicenseMutation.mutate(lic.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Malpractice Insurance */}
            <Separator />
            <div>
              <h3 className="font-semibold mb-3">Malpractice Insurance</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Carrier</p>
                  <p className="font-medium">{comp.malpractice_insurance_carrier || "—"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Policy Number</p>
                  <p className="font-medium font-mono">{comp.malpractice_policy_number || "—"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Expiration</p>
                  <p className="font-medium">
                    {comp.malpractice_expiration_date
                      ? format(new Date(comp.malpractice_expiration_date), "MMM d, yyyy")
                      : "—"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
